import React, { useState, useEffect, useMemo, useRef } from 'react';
import ReportTemplate from '../components/ReportTemplate';
import { useLocation } from 'react-router-dom';
import { useIsMobile } from '../hooks/useIsMobile';
import { supabase } from '../lib/supabase';

const s = {
  page: { padding: '28px 32px', background: 'var(--bg)', minHeight: '100vh', fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif", color: 'var(--text)' },
  title: { fontSize: 21, fontWeight: 700, color: 'var(--text)', letterSpacing: '-0.3px', margin: 0, marginBottom: 3 },
  subtitle: { fontSize: 13, color: 'var(--text2)', margin: 0, marginBottom: 24, fontWeight: 400 },
  steps: { display: 'flex', alignItems: 'center', marginBottom: 24 },
  stepActive: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#a78bfa', fontWeight: 500 },
  stepInactive: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text2)', fontWeight: 500 },
  stepDone: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#22c55e', fontWeight: 500 },
  stepNumActive: { width: 22, height: 22, borderRadius: '50%', border: '1.5px solid #7c3aed', background: 'color-mix(in srgb, #7c3aed 15%, var(--bg))', color: '#a78bfa', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, flexShrink: 0 },
  stepNumInactive: { width: 22, height: 22, borderRadius: '50%', border: '1.5px solid #2a2e45', background: 'var(--bg)', color: 'var(--text2)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, flexShrink: 0 },
  stepNumDone: { width: 22, height: 22, borderRadius: '50%', border: '1.5px solid #22c55e', background: '#0f2018', color: '#22c55e', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 600, flexShrink: 0 },
  stepLine: { flex: 1, height: 1, background: 'var(--border)', margin: '0 10px', maxWidth: 36 },
  section: { background: 'var(--bg2)', border: '1px solid #1e2130', borderRadius: 10, padding: '16px 18px', marginBottom: 10 },
  sectionLabel: { fontSize: 10, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: 'var(--text3)', marginBottom: 12, display: 'block' },
  chips: { display: 'flex', flexWrap: 'wrap', gap: 6 },
  chip: { padding: '7px 13px', borderRadius: 7, border: '1px solid #1e2130', background: 'var(--bg)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: 'var(--text2)', transition: 'all 0.12s', userSelect: 'none' },
  chipSel: { padding: '7px 13px', borderRadius: 7, border: '1px solid #6d28d9', background: 'color-mix(in srgb, #7c3aed 12%, var(--bg))', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 500, color: '#a78bfa', transition: 'all 0.12s', userSelect: 'none' },
  initials: { width: 22, height: 22, borderRadius: 5, background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: 'var(--text2)', flexShrink: 0 },
  initialsSel: { width: 22, height: 22, borderRadius: 5, background: 'color-mix(in srgb, #7c3aed 25%, var(--bg))', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 700, color: '#a78bfa', flexShrink: 0 },
  periodRow: { display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap', flexDirection: 'row' },
  dateGroup: { display: 'flex', flexDirection: 'column', gap: 4 },
  dateLabel: { fontSize: 11, fontWeight: 500, color: 'var(--text2)' },
  dateInput: { background: 'var(--bg)', border: '1px solid #1e2130', borderRadius: 7, padding: '7px 10px', fontSize: 13, color: 'var(--text)', fontFamily: "inherit", outline: 'none', width: 140 },
  shortcuts: { display: 'flex', gap: 5, flexWrap: 'wrap', paddingBottom: 1 },
  scut: { padding: '6px 10px', borderRadius: 7, border: '1px solid #1e2130', background: 'var(--bg)', cursor: 'pointer', fontSize: 12, fontWeight: 500, color: 'var(--text2)', fontFamily: 'inherit' },
  scutActive: { padding: '6px 10px', borderRadius: 7, border: '1px solid #6d28d9', background: 'color-mix(in srgb, #7c3aed 12%, var(--bg))', cursor: 'pointer', fontSize: 12, fontWeight: 500, color: '#a78bfa', fontFamily: 'inherit' },
  reportGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 8 },
  reportCard: { border: '1px solid #1e2130', borderRadius: 9, background: 'var(--bg)', padding: '14px', cursor: 'pointer', transition: 'all 0.12s' },
  reportCardSel: { border: '1px solid #6d28d9', borderRadius: 9, background: 'color-mix(in srgb, #7c3aed 10%, var(--bg))', padding: '14px', cursor: 'pointer', transition: 'all 0.12s' },
  reportIcon: { width: 28, height: 28, borderRadius: 7, background: 'var(--surface)', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 },
  reportIconSel: { width: 28, height: 28, borderRadius: 7, background: 'color-mix(in srgb, #7c3aed 18%, var(--bg))', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 },
  reportTitle: { fontSize: 13, fontWeight: 600, color: 'var(--text)', marginBottom: 3 },
  reportTitleSel: { fontSize: 13, fontWeight: 600, color: '#a78bfa', marginBottom: 3 },
  reportDesc: { fontSize: 11, fontWeight: 400, color: 'var(--text2)', lineHeight: 1.5 },
  titleInput: { width: '100%', background: 'var(--bg)', border: '1px solid #1e2130', borderRadius: 8, padding: '9px 12px', fontSize: 13, color: 'var(--text)', fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' },
  ctaBtn: { width: '100%', padding: '11px', borderRadius: 9, border: 'none', background: '#7c3aed', cursor: 'pointer', fontSize: 14, fontWeight: 600, color: 'white', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 14 },
  ctaBtnDisabled: { width: '100%', padding: '11px', borderRadius: 9, border: 'none', background: 'color-mix(in srgb, #7c3aed 20%, var(--bg2))', cursor: 'not-allowed', fontSize: 14, fontWeight: 600, color: 'color-mix(in srgb, #7c3aed 50%, var(--text3))', fontFamily: 'inherit', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, marginTop: 14 },
  badge: { width: 14, height: 14, borderRadius: 3, fontSize: 8, fontWeight: 700, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  badgeMeta: { background: 'rgba(24,119,242,0.12)', color: '#4a9eff' },
  badgeGoogle: { background: 'rgba(52,168,83,0.12)', color: '#34a853' },

  // Step 2 - Campaigns
  campaignTable: { width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' },
  campaignRow: { borderBottom: '1px solid #1a1d2e', cursor: 'pointer' },
  campaignCell: { padding: '10px 12px', fontSize: 13, color: 'var(--text2)' },
  campaignName: { padding: '10px 12px', fontSize: 13, color: 'var(--text)', fontWeight: 500 },
  checkBox: { width: 16, height: 16, borderRadius: 4, border: '1.5px solid #2a2e45', background: 'var(--bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' },
  checkBoxChecked: { width: 16, height: 16, borderRadius: 4, border: '1.5px solid #7c3aed', background: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, cursor: 'pointer' },
  tableHeader: { padding: '8px 12px', fontSize: 10, fontWeight: 700, letterSpacing: '0.8px', textTransform: 'uppercase', color: 'var(--text3)', borderBottom: '1px solid #1e2130', textAlign: 'left' },
  statusBadge: { padding: '2px 7px', borderRadius: 4, fontSize: 11, fontWeight: 600 },

  // Step 3 - Report
  reportOutput: { background: 'var(--bg2)', border: '1px solid #1e2130', borderRadius: 10, padding: '24px', marginBottom: 12, lineHeight: 1.7, fontSize: 14, color: 'var(--text)', whiteSpace: 'pre-wrap' },
  loadingBox: { display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '48px 24px', gap: 16 },
  spinner: { width: 32, height: 32, border: '3px solid #1e2130', borderTop: '3px solid #7c3aed', borderRadius: '50%', animation: 'spin 0.8s linear infinite' },
  actionRow: { display: 'flex', gap: 8, marginTop: 12 },
  secBtn: { padding: '9px 18px', borderRadius: 8, border: '1px solid #1e2130', background: 'var(--bg)', cursor: 'pointer', fontSize: 13, fontWeight: 500, color: 'var(--text2)', fontFamily: 'inherit' },
  primBtn: { padding: '9px 18px', borderRadius: 8, border: 'none', background: '#7c3aed', cursor: 'pointer', fontSize: 13, fontWeight: 600, color: 'white', fontFamily: 'inherit' },
};

function getInitials(name) {
  return name.split(' ').map(w => w[0]).join('').substring(0, 3).toUpperCase();
}

function formatCurrency(v) {
  if (!v && v !== 0) return '—';
  return '€' + Number(v).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function formatNumber(v) {
  if (!v && v !== 0) return '—';
  return Number(v).toLocaleString('it-IT');
}

const REPORT_TYPES = [
  { id: 'performance', label: 'Performance', desc: 'Analisi risultati campagne', icon: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
      <rect x="1" y="8" width="3" height="7" rx="1" fill="#a78bfa"/>
      <rect x="6" y="5" width="3" height="10" rx="1" fill="#a78bfa" opacity="0.65"/>
      <rect x="11" y="1" width="3" height="14" rx="1" fill="#a78bfa" opacity="0.4"/>
    </svg>
  )},
  { id: 'mensile', label: 'Mensile', desc: 'Riepilogo mese corrente', icon: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#3b4260" strokeWidth="1.3">
      <rect x="1.5" y="1.5" width="13" height="13" rx="2"/>
      <line x1="1.5" y1="6" x2="14.5" y2="6"/>
      <line x1="8" y1="6" x2="8" y2="14.5"/>
    </svg>
  )},
  { id: 'campagna', label: 'Campagna', desc: 'Focus campagna specifica', icon: (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="#3b4260" strokeWidth="1.3">
      <circle cx="8" cy="8" r="6"/>
      <path d="M8 8 L8 3.5" strokeLinecap="round"/>
      <path d="M8 8 L11.5 10" strokeLinecap="round"/>
    </svg>
  )},
];

export default function Generator() {
  const location = useLocation();
  const isMobile = useIsMobile();
  const [step, setStep] = useState(1);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [dateFrom, setDateFrom] = useState('2025-01-01');
  const [dateTo, setDateTo] = useState('2025-12-31');
  const [activeShortcut, setActiveShortcut] = useState(2025);
  const [reportType, setReportType] = useState('performance');
  const [reportTitle, setReportTitle] = useState('');
  const [campaigns, setCampaigns] = useState([]);
  const [campaignInsights, setCampaignInsights] = useState({});
  const [dailyData, setDailyData] = useState([]);
  const [campaignImage, setCampaignImage] = useState(null);
  const [mockupImageUrl, setMockupImageUrl] = useState(null);
  const [demographics, setDemographics] = useState({ gender: [], age: [] });
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [search, setSearch] = useState('');
  const [pageSize, setPageSize] = useState(20);
  const [onlyWithData, setOnlyWithData] = useState(true);
  const [visibleKpis, setVisibleKpis] = useState(['spend', 'impressions', 'clicks']);
  const [selectedCampaigns, setSelectedCampaigns] = useState([]);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [report, setReport] = useState('');
  const [loadingReport, setLoadingReport] = useState(false);
  const [error, setError] = useState(null);
  const [uploadingImage, setUploadingImage] = useState(null); // campaign id in upload

  async function handleImageUpload(campaign, file) {
    if (!file) return;
    setUploadingImage(campaign.id);
    try {
      const ext = file.name.split('.').pop() || 'jpg';
      const path = `manual/${campaign.id}.${ext}`;
      const { data, error } = await supabase.storage
        .from('campaign-media')
        .upload(path, file, { upsert: true, contentType: file.type });
      if (error) throw error;
      const { data: urlData } = supabase.storage.from('campaign-media').getPublicUrl(path);
      await supabase.from('campaigns').update({ preview_image_url: urlData.publicUrl }).eq('id', campaign.id);
      setCampaigns(prev => prev.map(c => c.id === campaign.id ? { ...c, preview_image_url: urlData.publicUrl } : c));
    } catch(e) {
      alert('Errore upload: ' + e.message);
    } finally {
      setUploadingImage(null);
    }
  }

  const visibleCampaigns = useMemo(() => {
    return [...campaigns]
      .filter(c => {
        if (!c.start_date) return (campaignInsights[c.id]?.spend||0) > 0;
        const start = new Date(c.start_date);
        const end = c.end_date ? new Date(c.end_date) : null;
        return start <= new Date(dateTo) && (!end || end >= new Date(dateFrom));
      })
      .filter(c => c.name.toLowerCase().includes(search.toLowerCase()))
      .sort((a, b) => (campaignInsights[b.id]?.spend||0) - (campaignInsights[a.id]?.spend||0))
      .slice(0, pageSize);
  }, [campaigns, search, pageSize, campaignInsights, dateFrom, dateTo]);

  async function loadAllInsights(clientOverride) {
    const client = clientOverride || selectedClient;
    if (!client) return;
    setLoadingInsights(true);
    try {
      // Carica adsets per questo client
      const { data: adsets } = await supabase
        .from('adsets').select('id, campaign_id')
        .eq('client_id', client.id).eq('platform', 'google').limit(10000);
      if (!adsets?.length) { setLoadingInsights(false); return; }
      const adsetToCampaign = {};
      adsets.forEach(a => { adsetToCampaign[a.id] = a.campaign_id; });
      // Carica tutti gli insights nel periodo per questo client
      const { data: insights } = await supabase
        .from('adset_insights_daily').select('adset_id, spend, impressions, clicks')
        .eq('client_id', client.id)
        .limit(10000);
      const totals = {};
      (insights || []).forEach(i => {
        const cid = adsetToCampaign[i.adset_id]; if (!cid) return;
        if (!totals[cid]) totals[cid] = { spend:0, impressions:0, clicks:0 };
        totals[cid].spend += parseFloat(i.spend)||0;
        totals[cid].impressions += parseInt(i.impressions)||0;
        totals[cid].clicks += parseInt(i.clicks)||0;
      });
      setCampaignInsights(totals);
      // Daily trend
      const { data: daily } = await supabase.from("adset_insights_daily").select("date, spend, impressions, clicks").eq("client_id", client.id).gte("date", dateFrom).lte("date", dateTo).order("date");
      const byDay = {}; (daily||[]).forEach(r => { if (!byDay[r.date]) byDay[r.date] = {date:r.date,spend:0,impressions:0,clicks:0}; byDay[r.date].spend+=parseFloat(r.spend)||0; byDay[r.date].impressions+=parseInt(r.impressions)||0; byDay[r.date].clicks+=parseInt(r.clicks)||0; }); setDailyData(Object.values(byDay).sort((a,b)=>a.date.localeCompare(b.date)));
      // Demographics
      const { data: demoData } = await supabase.from("adset_demographics_daily").select("gender, age_range, impressions, clicks, spend").eq("client_id", client.id).gte("date", dateFrom).lte("date", dateTo);
      const gMap={}, aMap={}; (demoData||[]).forEach(r=>{ if(r.gender!=="ALL"){if(!gMap[r.gender])gMap[r.gender]={gender:r.gender,impressions:0,clicks:0,spend:0}; gMap[r.gender].impressions+=parseInt(r.impressions)||0; gMap[r.gender].clicks+=parseInt(r.clicks)||0; gMap[r.gender].spend+=parseFloat(r.spend)||0;} if(r.age_range!=="ALL"&&r.age_range!=="AGE_RANGE_UNDETERMINED"){if(!aMap[r.age_range])aMap[r.age_range]={age_range:r.age_range,impressions:0,clicks:0,spend:0}; aMap[r.age_range].impressions+=parseInt(r.impressions)||0; aMap[r.age_range].clicks+=parseInt(r.clicks)||0; aMap[r.age_range].spend+=parseFloat(r.spend)||0;} }); const ageOrder=["AGE_RANGE_18_24","AGE_RANGE_25_34","AGE_RANGE_35_44","AGE_RANGE_45_54","AGE_RANGE_55_64","AGE_RANGE_65_UP"]; setDemographics({gender:Object.values(gMap),age:ageOrder.map(k=>aMap[k]).filter(Boolean)});
    } catch(e) { console.error(e); }
    finally { setLoadingInsights(false); }
  }

  // Mantieni per compatibilità
  async function loadInsightsForVisible(visibleCamps, clientOverride) {
    return loadAllInsights(clientOverride);
  }

  useEffect(() => {
    if (campaigns.length > 0 && step === 2 && selectedClient) {
      loadAllInsights(selectedClient);
    }
  }, [dateFrom, dateTo, step, selectedClient]); // eslint-disable-line

  useEffect(() => { loadClients(); }, []);

  // Pre-seleziona cliente da URL (?client=ID)
  useEffect(() => {
    if (clients.length === 0) return;
    const params = new URLSearchParams(location.search);
    const clientId = params.get('client');
    if (clientId) {
      const found = clients.find(c => String(c.id) === clientId);
      if (found) setSelectedClient(found);
    }
  }, [clients, location.search]);

  const years = Array.from({ length: new Date().getFullYear() - 2023 }, (_, i) => 2024 + i);

  async function loadClients() {
    const { data } = await supabase.from('clients').select('*').order('name');
    if (data) { const hidden = JSON.parse(localStorage.getItem('sidebar_hidden') || '[]'); setClients(data.filter(c => !hidden.includes(c.id))); }
  }

  function toggleKpi(kpi) {
    setVisibleKpis(prev => prev.includes(kpi) ? prev.filter(k => k !== kpi) : [...prev, kpi]);
  }

  async function loadCampaigns() {
    if (!selectedClient) return;
    setLoadingCampaigns(true);
    setCampaignInsights({});
    setError(null);
    try {
      const { data: adAccounts } = await supabase
        .from('ad_accounts')
        .select('id, platform')
        .eq('client_id', selectedClient.id);

      if (!adAccounts?.length) { setCampaigns([]); setLoadingCampaigns(false); return; }

      const accountIds = adAccounts.map(a => a.id);

      // Carica solo la lista campagne (senza insights — leggero)
      const { data: camps, error: campsError } = await supabase
        .from('campaigns')
        .select('id, name, status, platform, ad_account_id, ad_accounts(name, platform_account_id), preview_image_url, ad_headline, ad_description, ad_final_url, start_date, end_date')
        .in('ad_account_id', accountIds)
        .neq('status', 'DELETED')
        .order('name')
        .limit(2000);

      if (campsError) { setError('Errore caricamento campagne'); setLoadingCampaigns(false); return; }
      setCampaigns(camps || []);
      setSelectedCampaigns([]);
      // Carica insights per le prime N campagne visibili
      if (camps?.length) loadAllInsights(selectedClient);
    } catch(e) {
      setError('Errore: ' + e.message);
    } finally {
      setLoadingCampaigns(false);
    }
  }

  function setShortcut(type) {
    const now = new Date();
    setActiveShortcut(type);
    if (type === 'last_month') {
      const from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
      const to = new Date(now.getFullYear(), now.getMonth(), 0);
      setDateFrom(from.toISOString().split('T')[0]);
      setDateTo(to.toISOString().split('T')[0]);
    } else if (type === '3months') {
      const from = new Date(now); from.setMonth(from.getMonth() - 3);
      setDateFrom(from.toISOString().split('T')[0]);
      setDateTo(now.toISOString().split('T')[0]);
    } else if (typeof type === 'number') {
      setDateFrom(`${type}-01-01`);
      setDateTo(`${type}-12-31`);
    }
  }

  function toggleCampaign(id) {
    setSelectedCampaigns(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }

  async function compressAndUploadImage(file, clientId) {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => resolve(e.target.result);
      reader.onerror = () => resolve(null);
      reader.readAsDataURL(file);
    });
  }

  async function generateReport() {
    setStep(3);
  }

  function getClientPlatforms(client) {
    // Mostra badge piattaforme se disponibili
    return [];
  }

  const Step = ({ num, label, current, done }) => {
    const style = done ? s.stepDone : current ? s.stepActive : s.stepInactive;
    const numStyle = done ? s.stepNumDone : current ? s.stepNumActive : s.stepNumInactive;
    return (
      <div style={style}>
        <div style={numStyle}>{done ? '✓' : num}</div>
        {label}
      </div>
    );
  };

  return (
    <div style={s.page}>
      <style>{`@keyframes spin { to { transform: rotate(360deg); } } input[type=date]::-webkit-calendar-picker-indicator { filter: invert(0.5); }`}</style>

      <h1 style={s.title}>Genera Report</h1>
      <p style={s.subtitle}>Seleziona cliente, periodo e campagne — AI genera il report</p>

      <div style={s.steps}>
        <Step num={1} label="Configurazione" current={step === 1} done={step > 1} />
        <div style={s.stepLine} />
        <Step num={2} label="Campagne" current={step === 2} done={step > 2} />
        <div style={s.stepLine} />
        <Step num={3} label="Report" current={step === 3} done={false} />
      </div>

      {/* ── STEP 1 ── */}
      {step === 1 && (
        <>
          {/* Cliente */}
          <div style={s.section}>
            <span style={s.sectionLabel}>Cliente</span>
            <div style={s.chips}>
              {clients.map(c => {
                const sel = selectedClient?.id === c.id;
                return (
                  <div key={c.id} style={sel ? s.chipSel : s.chip} onClick={() => setSelectedClient(c)}>
                    <div style={sel ? s.initialsSel : s.initials}>{getInitials(c.name)}</div>
                    {c.name}
                  </div>
                );
              })}
            </div>
          </div>


          {/* Immagine campagna (opzionale) */}
          <div style={s.section}>
            <span style={s.sectionLabel}>Immagine campagna <span style={{color:'var(--text3)',fontWeight:400,fontSize:11}}>(opzionale — per il mockup nel report)</span></span>
            <div style={{display:'flex',alignItems:'center',gap:12,marginTop:8}}>
              <label style={{...s.scut,cursor:'pointer',display:'flex',alignItems:'center',gap:6}}>
                <span>📎</span>
                <span>{campaignImage ? campaignImage.name : 'Carica immagine'}</span>
                <input type="file" accept="image/*" style={{display:'none'}} onChange={e => setCampaignImage(e.target.files[0])} />
              </label>
              {campaignImage && <button style={{...s.scut,color:'#f87171',borderColor:'#f87171'}} onClick={()=>setCampaignImage(null)}>✕ Rimuovi</button>}
            </div>
          </div>

          {/* Tipo Report */}
          <div style={s.section}>
            <span style={s.sectionLabel}>Tipo Report</span>
            <div style={s.reportGrid}>
              {REPORT_TYPES.map(rt => {
                const sel = reportType === rt.id;
                return (
                  <div key={rt.id} style={sel ? s.reportCardSel : s.reportCard} onClick={() => setReportType(rt.id)}>
                    <div style={sel ? s.reportIconSel : s.reportIcon}>{rt.icon}</div>
                    <div style={sel ? s.reportTitleSel : s.reportTitle}>{rt.label}</div>
                    <div style={s.reportDesc}>{rt.desc}</div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Titolo */}
          <div style={s.section}>
            <span style={s.sectionLabel}>Titolo <span style={{ fontWeight: 400, textTransform: 'none', letterSpacing: 0, fontSize: 11, color: 'var(--text3)' }}>— opzionale</span></span>
            <input style={s.titleInput} type="text" value={reportTitle}
              onChange={e => setReportTitle(e.target.value)}
              placeholder={`es. Performance ${new Date().getFullYear()} — ${selectedClient?.name || 'Cliente'}`} />
          </div>

          <button
            style={selectedClient ? s.ctaBtn : s.ctaBtnDisabled}
            disabled={!selectedClient}
            onClick={() => { setStep(2); loadCampaigns(); }}
          >
            Continua — Seleziona Campagne →
          </button>
        </>
      )}

      {/* ── STEP 2 ── */}
      {step === 2 && (
        <>
          <div style={s.section}>
            {/* Periodo */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14, flexWrap: 'wrap' }}>
              <div style={s.dateGroup}>
                <span style={s.dateLabel}>Dal</span>
                <input style={s.dateInput} type="date" value={dateFrom}
                  onChange={e => { setDateFrom(e.target.value); setActiveShortcut(null); }} />
              </div>
              <span style={{ color: 'var(--text3)', fontSize: 16 }}>→</span>
              <div style={s.dateGroup}>
                <span style={s.dateLabel}>Al</span>
                <input style={s.dateInput} type="date" value={dateTo}
                  onChange={e => { setDateTo(e.target.value); setActiveShortcut(null); }} />
              </div>
              <div style={s.shortcuts}>
                <button style={activeShortcut === 'last_month' ? s.scutActive : s.scut} onClick={() => setShortcut('last_month')}>Ultimo mese</button>
                <button style={activeShortcut === '3months' ? s.scutActive : s.scut} onClick={() => setShortcut('3months')}>3 mesi</button>
                {years.map(y => (
                  <button key={y} style={activeShortcut === y ? s.scutActive : s.scut} onClick={() => setShortcut(y)}>{y}</button>
                ))}
              </div>
            </div>

            {/* Toolbar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              <span style={{ ...s.sectionLabel, margin: 0, flex: 'none' }}>
                Campagne ({selectedCampaigns.length}/{campaigns.filter(c => c.name.toLowerCase().includes(search.toLowerCase())).length})
              </span>
              <input
                type="text"
                placeholder="Cerca campagna…"
                value={search}
                onChange={e => setSearch(e.target.value)}
                style={{ flex: 1, minWidth: 160, background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: 7, padding: '6px 10px', fontSize: 12, color: 'var(--text)', fontFamily: 'inherit', outline: 'none' }}
              />
              <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                {[
                  { key: 'spend', label: 'Spend' },
                  { key: 'impressions', label: 'Impression' },
                  { key: 'clicks', label: 'Click' },
                ].map(({ key, label }) => (
                  <button key={key}
                    style={{ padding: '5px 9px', borderRadius: 6, border: `1px solid ${visibleKpis.includes(key) ? '#6d28d9' : 'var(--border)'}`, background: visibleKpis.includes(key) ? 'color-mix(in srgb, #7c3aed 12%, var(--bg))' : 'var(--bg)', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: visibleKpis.includes(key) ? '#a78bfa' : 'var(--text3)', fontFamily: 'inherit' }}
                    onClick={() => toggleKpi(key)}
                  >{label}</button>
                ))}
                <button
                  onClick={() => setOnlyWithData(p => !p)}
                  style={{ padding:'5px 9px', borderRadius:6, border:`1px solid ${onlyWithData?'#6d28d9':'var(--border)'}`, background: onlyWithData?'color-mix(in srgb,#7c3aed 12%,var(--bg))':'var(--bg)', cursor:'pointer', fontSize:11, fontWeight:600, color:onlyWithData?'#a78bfa':'var(--text3)', fontFamily:'inherit', whiteSpace:'nowrap' }}
                >Solo con dati</button>
                <button style={s.scut} onClick={() => setSelectedCampaigns(campaigns.map(c => c.id))}>Tutte</button>
                <button style={s.scut} onClick={() => setSelectedCampaigns([])}>Nessuna</button>
                <button
                  onClick={() => setOnlyWithData(p => !p)}
                  style={{ padding: '5px 9px', borderRadius: 6, border: `1px solid ${onlyWithData ? '#6d28d9' : 'var(--border)'}`, background: onlyWithData ? 'color-mix(in srgb, #7c3aed 12%, var(--bg))' : 'var(--bg)', cursor: 'pointer', fontSize: 11, fontWeight: 600, color: onlyWithData ? '#a78bfa' : 'var(--text3)', fontFamily: 'inherit', whiteSpace: 'nowrap' }}
                >Solo con dati</button>
                <select value={pageSize} onChange={e => setPageSize(Number(e.target.value))} style={{ padding: '5px 8px', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--bg)', color: 'var(--text2)', fontSize: 11, fontFamily: 'inherit', cursor: 'pointer' }}>
                  {[10,20,50,100,200].map(n => <option key={n} value={n}>{n} righe</option>)}
                </select>
              </div>
            </div>

            {loadingCampaigns ? (
              <div style={s.loadingBox}>
                <div style={s.spinner} />
                <span style={{ fontSize: 13, color: 'var(--text2)' }}>Caricamento campagne…</span>
              </div>
            ) : campaigns.length === 0 ? (
              <div style={{ padding: '24px', textAlign: 'center', color: 'var(--text2)', fontSize: 13 }}>
                Nessuna campagna trovata per questo cliente nel periodo selezionato.
              </div>
            ) : (
              <table style={s.campaignTable}>
                <thead>
                  <tr>
                    <th style={{ ...s.tableHeader, width: 36 }}></th>
                    <th style={s.tableHeader}>Campagna</th>
                    <th style={s.tableHeader}>Piattaforma</th>
                    <th style={s.tableHeader}>AdAccount</th>
                    <th style={{...s.tableHeader, width:80, textAlign:'center'}}>Inizio</th>
                    <th style={{...s.tableHeader, width:80, textAlign:'center'}}>Fine</th>
                    <th style={{...s.tableHeader, width:60, textAlign:'center'}}>Img</th>
                    <th style={{ ...s.tableHeader, textAlign: 'right' }}>Spend</th>
                    <th style={{ ...s.tableHeader, textAlign: 'right' }}>Impression</th>
                    <th style={{ ...s.tableHeader, textAlign: 'right' }}>Click</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleCampaigns.map(c => {
                    const checked = selectedCampaigns.includes(c.id);
                    return (
                      <tr key={c.id} style={s.campaignRow} onClick={() => toggleCampaign(c.id)}>
                        <td style={{ ...s.campaignCell, paddingLeft: 12 }}>
                          <div style={checked ? s.checkBoxChecked : s.checkBox}>
                            {checked && <svg width="10" height="8" viewBox="0 0 10 8" fill="none"><path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                          </div>
                        </td>
                        <td style={s.campaignName}>{c.name}</td>
                        <td style={s.campaignCell}>
                          <span style={{ ...s.statusBadge, background: c.platform === 'meta' ? 'rgba(24,119,242,0.1)' : c.platform === 'google' ? 'rgba(52,168,83,0.1)' : 'rgba(255,255,255,0.05)', color: c.platform === 'meta' ? '#4a9eff' : c.platform === 'google' ? '#34a853' : 'var(--text2)' }}>
                            {c.platform}
                          </span>
                        </td>
                        <td style={{ ...s.campaignCell, fontSize: 11, color: 'var(--text3)' }}>
                          {c.ad_accounts?.name || c.ad_account_id?.substring(0,8)}
                        </td>
                        {<td style={{...s.campaignCell, textAlign:'center', fontSize:11, color:'var(--text3)'}}>{c.start_date?new Date(c.start_date).toLocaleDateString('it-IT',{day:'2-digit',month:'2-digit',year:'2-digit'}):'—'}</td>}
                        {<td style={{...s.campaignCell, textAlign:'center', fontSize:11, color:'var(--text3)'}}>{c.end_date?new Date(c.end_date).toLocaleDateString('it-IT',{day:'2-digit',month:'2-digit',year:'2-digit'}):'in corso'}</td>}
                        <td style={{...s.campaignCell, textAlign:'center'}} onClick={e => e.stopPropagation()}>
                          {uploadingImage === c.id ? (
                            <span style={{fontSize:10,color:'var(--text3)'}}>…</span>
                          ) : c.preview_image_url ? (
                            <div style={{position:'relative',display:'inline-block'}}>
                              <img src={c.preview_image_url} alt="" style={{width:36,height:24,objectFit:'cover',borderRadius:3,display:'block'}}
                                onError={e => e.target.style.display='none'}/>
                              <label style={{position:'absolute',inset:0,cursor:'pointer',opacity:0}} title="Sostituisci immagine">
                                <input type="file" accept="image/*" style={{display:'none'}} onChange={e => handleImageUpload(c, e.target.files[0])}/>
                              </label>
                            </div>
                          ) : (
                            <label style={{cursor:'pointer',display:'inline-flex',alignItems:'center',justifyContent:'center',
                              width:32,height:22,borderRadius:3,border:'1px dashed var(--border)',
                              color:'var(--text3)',fontSize:14,lineHeight:1}} title="Carica immagine">
                              +
                              <input type="file" accept="image/*" style={{display:'none'}} onChange={e => handleImageUpload(c, e.target.files[0])}/>
                            </label>
                          )}
                        </td>
                        {visibleKpis.includes('spend') && <td style={{ ...s.campaignCell, textAlign: 'right', color: (campaignInsights[c.id]?.spend || 0) > 0 ? 'var(--text)' : 'var(--text3)' }}>{loadingInsights ? '…' : formatCurrency(campaignInsights[c.id]?.spend || 0)}</td>}
                        {visibleKpis.includes('impressions') && <td style={{ ...s.campaignCell, textAlign: 'right' }}>{loadingInsights ? '…' : formatNumber(campaignInsights[c.id]?.impressions || 0)}</td>}
                        {visibleKpis.includes('clicks') && <td style={{ ...s.campaignCell, textAlign: 'right' }}>{loadingInsights ? '…' : formatNumber(campaignInsights[c.id]?.clicks || 0)}</td>}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {error && <div style={{ color: '#f87171', fontSize: 13, marginBottom: 10, padding: '10px 14px', background: '#1a0a0a', borderRadius: 8, border: '1px solid #3a1515' }}>{error}</div>}

          <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
            <button style={{ ...s.secBtn, flex: 1 }} onClick={() => setStep(1)}>← Indietro</button>
            <button style={{ ...s.primBtn, flex: 2, opacity: selectedCampaigns.length === 0 ? 0.5 : 1 }}
              disabled={selectedCampaigns.length === 0}
              onClick={() => { setStep(3); generateReport(); }}>
              Genera Report →
            </button>
          </div>
        </>
      )}

      {/* ── STEP 3 ── */}
      {step === 3 && (
        <>
          {loadingReport ? (
            <div style={{ ...s.section, ...s.loadingBox }}>
              <div style={s.spinner} />
              <span style={{ fontSize: 14, color: 'var(--text2)', fontWeight: 500 }}>AI sta generando il report…</span>
              <span style={{ fontSize: 12, color: 'var(--text2)' }}>Analisi di {selectedCampaigns.length} campagne per {selectedClient?.name}</span>
            </div>
          ) : error ? (
            <div style={{ ...s.section, color: '#f87171' }}>{error}</div>
          ) : (
            <>
              <div style={s.section}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                  <div>
                    <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text)', marginBottom: 2 }}>
                      {reportTitle || `Report ${reportType} — ${selectedClient?.name}`}
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text2)' }}>{dateFrom} → {dateTo} · {selectedCampaigns.length} campagne</div>
                  </div>
                </div>
                <div id="report-output">
                <ReportTemplate
                  client={selectedClient}
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                  campaigns={campaigns.filter(c => selectedCampaigns.includes(c.id) && (campaignInsights[c.id]?.spend||0) > 0)}
                  campaignInsights={campaignInsights}
                  dailyData={dailyData}
                  mockupImageUrl={mockupImageUrl || (() => {
                    const withImg = campaigns.filter(c => selectedCampaigns.includes(c.id) && c.preview_image_url);
                    const withSpend = campaigns.filter(c => selectedCampaigns.includes(c.id) && (campaignInsights[c.id]?.spend||0) > 0 && c.preview_image_url);
                    return (withSpend[0] || withImg[0])?.preview_image_url || null;
                  })()}
                  demographics={demographics}
                />
                </div>
              </div>

              <div style={s.actionRow}>
                <button style={s.secBtn} onClick={() => { setStep(2); setReport(''); setMockupImageUrl(null); }}>← Modifica selezione</button>
                <button style={s.secBtn} onClick={() => { setStep(1); setReport(''); setSelectedClient(null); setSelectedCampaigns([]); }}>Nuovo report</button>
              <button style={{ ...s.secBtn, background: 'color-mix(in srgb, #22c55e 15%, var(--bg))', borderColor: '#22c55e', color: '#22c55e' }} onClick={() => window.print()}>🖨 Stampa / Salva PDF</button>
                <button style={s.primBtn} onClick={() => {
                  const el = document.getElementById('report-output');
                  const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${reportTitle || 'Report'}</title><link href="https://fonts.googleapis.com/css2?family=Montserrat:wght@400;600;700;800;900&display=swap" rel="stylesheet"><style>body{margin:0;font-family:'Montserrat',sans-serif;-webkit-print-color-adjust:exact}</style></head><body>${el ? el.innerHTML : ''}</body></html>`;
                  const blob = new Blob([html], { type: 'text/html' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a'); a.href = url;
                  a.download = (reportTitle || selectedClient?.name || 'report') + '-' + dateFrom + '.html'; a.click();
                }}>↓ Scarica</button>
              </div>
            </>
          )}
        </>
      )}
    </div>
  );
}
